import React, { type ChangeEvent, useContext } from 'react';
import { Editor, Node, Transforms, Element } from 'slate';
import { useSlateStatic, useReadOnly, ReactEditor } from 'slate-react';
import classnames from 'classnames';
import ListContext from './ListContext';
import {
  getFirstTextChildMarks,
  getStyleFromChildMarks,
} from '../../../util/getStyleFromChildMarks';
import { isListItem } from '../guards';
import { shouldHideMarker } from '../utils';
import {
  type ComponentProps,
  type ListItemElement,
} from '../../../types/aha-editor';
import styles from './ListStyles.module.css';

const ListItem: React.FC<ComponentProps<ListItemElement>> = ({
  children,
  element,
  attributes,
}) => {
  const editor = useSlateStatic();
  const readOnly = useReadOnly();
  const type = useContext(ListContext);

  if (!isListItem(element)) return null;

  const style = getStyleFromChildMarks(element);
  const checked = element.list_checked;

  const onChecked = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const path = ReactEditor.findPath(editor, element);

    Transforms.setNodes(
      editor,
      { list_checked: event.target.checked },
      { at: path }
    );
  };

  const classesFromChildMarks = () => {
    return Object.keys(getFirstTextChildMarks(element))
      .map((e) => `${e}-child`)
      .join(' ');
  };

  const classesFromChildBlock = () => {
    const nodeEntry = Node.texts(element).next();
    if (!nodeEntry?.value) {
      return '';
    }

    const elementPath = ReactEditor.findPath(editor, element);
    const path = [...elementPath, ...nodeEntry.value[1]];

    const blockNodeEntry = Editor.above<Element>(editor, {
      at: path,
      match: (n) => Element.isElement(n) && Editor.isBlock(editor, n),
    });
    if (!blockNodeEntry) return '';

    return `${blockNodeEntry[0].type}-child`;
  };

  const innerAttributes = readOnly
    ? { disabled: true }
    : { onChange: onChecked };

  let Tag: 'li' | 'dt' | 'dd' = 'li';
  if (type === 'dl') {
    // Use the child index to alternate the tag.
    const path = ReactEditor.findPath(editor, element);
    if (path[path.length - 1] % 2 === 0) {
      Tag = 'dt';
    } else {
      Tag = 'dd';
    }
  }

  const noMarker = shouldHideMarker(element);
  const classes = classnames(
    {
      [styles.liCheckbox]: type === 'checklist',
      [styles.hideMarker]: noMarker,
    },
    classesFromChildMarks(),
    classesFromChildBlock()
  );

  let checkbox: React.ReactNode;
  if (type === 'checklist' && !noMarker) {
    // Get the first child element's type
    const firstChild = element.children[0];
    const firstChildType =
      (Element.isElement(firstChild) && firstChild.type) || 'paragraph';

    checkbox = (
      <span
        className={styles.checkboxContainer}
        contentEditable={false}
        data-checkbox-first-child-type={firstChildType}
      >
        <input type="checkbox" checked={!!checked} {...innerAttributes} />
      </span>
    );
  }

  return (
    <Tag className={classes} style={style} {...attributes}>
      {checkbox}
      {children}
    </Tag>
  );
};

export default ListItem;
